import { createPortal } from 'react-dom';
import { FaSearch } from 'react-icons/fa';
import { useEffect, useMemo, useRef, useState } from 'react';

const BLOCK = 'LStudio__app_command';

/*
    🔥 PALETA DE COMANDOS

    commands: [{ id, label, hint, icon, group, keywords, onRun, to }]

    ⚠️ LA BÚSQUEDA ES POR SUBSECUENCIA, NO POR includes()

    Con includes(), escribir "nusu" no encuentra "Nuevo usuario". Con
    subsecuencia sí: alcanza con que las letras aparezcan EN ORDEN,
    que es como la gente escribe en un buscador de comandos. Es la
    diferencia entre una paleta que se usa y una que se abandona.

    ⚠️ LA LISTA SE RECORRE CON FLECHAS, PERO EL FOCO NO SE MUEVE

    El foco se queda SIEMPRE en el input: si saltara a cada opción,
    habría que volver al campo para seguir escribiendo. El elemento
    activo se marca con aria-activedescendant, que es exactamente para
    esto.

    ⚠️ EL RESALTADO SE ARMA CON LOS ÍNDICES DEL MATCH

    Se reusan las posiciones que ya calculó el filtro, así no se
    recorre la cadena dos veces ni se arma una expresión regular por
    comando en cada tecla.
*/

// 🔥 Devuelve los índices de las letras que coinciden, o null
const onMatch = (text, query) => {

    if (!query) return [];

    const LOWER = text.toLowerCase();
    const Q = query.toLowerCase();

    const HITS = [];

    let cursor = 0;

    for (let i = 0; i < Q.length; i += 1) {

        const FOUND = LOWER.indexOf(Q[i], cursor);

        if (FOUND === -1) return null;

        HITS.push(FOUND);
        cursor = FOUND + 1;
    }

    return HITS;
};

const Highlight = ({ text, hits }) => {

    if (!hits?.length) return text;

    const SET = new Set(hits);

    return (
        <>
            {
                text.split('').map((char, i) => (
                    SET.has(i)  ? <mark key={i} className={`${BLOCK}__mark`}>{char}</mark>   : <span key={i}>{char}</span>
                ))
            }
        </>
    );
};

export const AppCommandPalette = ({
    isOpen,   onClose, commands = [],
    placeholder = 'Buscá una acción o una página…',
    emptyLabel = 'Sin resultados',   onNavigate = null, // (to) => navigate(to)
    footerHint = '↑↓ para moverte · Enter para ejecutar · Esc para cerrar',
}) => {

    const REF__useRefInput = useRef(null);
    const REF__useRefList = useRef(null);

    const [useQuery, setQuery] = useState('');
    const [useActive, setActive] = useState(0);

    // -------------------- 🎯 FILTRADO
    const MEMO__RESULTS = useMemo(() => {

        const SCORED = commands
            .map((command) => {

                const HAYSTACK = [command.label, ...(command.keywords ?? [])].join(' ');

                const HITS = onMatch(HAYSTACK, useQuery);

                if (HITS === null) return null;

                // 🔥 Menos dispersión = mejor coincidencia
                const SPREAD = HITS.length > 1 ? HITS[HITS.length - 1] - HITS[0] : 0;

                return {
                    ...command,
                    hits: onMatch(command.label, useQuery) ?? [],
                    score: SPREAD + (HITS[0] ?? 0),
                };
            }).filter(Boolean).sort((a, b) => a.score - b.score);

        return SCORED;

    }, [commands, useQuery]);

    // -------------------- 🎯 AGRUPADO
    const MEMO__GROUPS = useMemo(() => {

        const MAP = new Map();

        MEMO__RESULTS.forEach((item) => {
            const KEY = item.group ?? 'General';
            if (!MAP.has(KEY)) MAP.set(KEY, []);
            MAP.get(KEY).push(item);
        });

        return [...MAP.entries()];

    }, [MEMO__RESULTS]);

    // 🔥 Al filtrar, el activo vuelve arriba: si no, queda apuntando a un índice que ya no existe
    useEffect(() => setActive(0), [useQuery]);

    useEffect(() => {
        if (!isOpen) return;
        setQuery('');
        setActive(0);
        requestAnimationFrame(() => REF__useRefInput.current?.focus());
    }, [isOpen]);

    // 🔥 Mantiene visible la opción activa al recorrer con flechas
    useEffect(() => {
        if (!isOpen) return;
        REF__useRefList.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
    }, [useActive, isOpen]);

    const onRun = (command) => {

        onClose?.();

        if (command.to && onNavigate) onNavigate(command.to);

        command.onRun?.();
    };

    const onKeyDown = (event) => {

        if (event.key === 'Escape') { onClose?.(); return; }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((i) => (MEMO__RESULTS.length ? (i + 1) % MEMO__RESULTS.length : 0));
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((i) => (MEMO__RESULTS.length ? (i - 1 + MEMO__RESULTS.length) % MEMO__RESULTS.length : 0));
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const TARGET = MEMO__RESULTS[useActive];
            if (TARGET) onRun(TARGET);
        }
    };

    if (!isOpen) return null;

    let cursor = -1;

    return createPortal(
        <div className={BLOCK} role="dialog" aria-modal="true" aria-label="Paleta de comandos">

            <div className={`${BLOCK}__backdrop`} onClick={onClose} />

            <div className={`${BLOCK}__panel`}>

                <div className={`${BLOCK}__search`}>

                    <FaSearch className={`${BLOCK}__search_icon`} aria-hidden="true" />

                    <input   ref={REF__useRefInput} className={`${BLOCK}__input`}  type="text"
                        value={useQuery}  placeholder={placeholder}   role="combobox"
                        aria-expanded="true"  aria-controls={`${BLOCK}_list`}
                        aria-activedescendant={MEMO__RESULTS[useActive] ? `${BLOCK}_opt_${useActive}` : undefined}
                        onChange={(e) => setQuery(e.target.value)}  onKeyDown={onKeyDown}
                    />

                </div>

                <div ref={REF__useRefList} id={`${BLOCK}_list`} className={`${BLOCK}__list`} role="listbox">
                    {
                        MEMO__RESULTS.length === 0
                            ? <p className={`${BLOCK}__empty`}>{emptyLabel}</p>
                            : MEMO__GROUPS.map(([group, items]) => (
                                <div key={group} className={`${BLOCK}__group`}>

                                    <p className={`${BLOCK}__group_title`}>{group}</p>

                                    {
                                        items.map((item) => {

                                            cursor += 1;

                                            const INDEX = cursor;

                                            return (
                                                <div
                                                    key={item.id}
                                                    id={`${BLOCK}_opt_${INDEX}`}
                                                    className={`${BLOCK}__item`}
                                                    role="option"
                                                    aria-selected={INDEX === useActive}
                                                    data-active={INDEX === useActive}
                                                    onMouseEnter={() => setActive(INDEX)}
                                                    onClick={() => onRun(item)}
                                                >

                                                    {
                                                        item.icon && (
                                                            <span className={`${BLOCK}__icon`} aria-hidden="true">
                                                                {item.icon}
                                                            </span>
                                                        )
                                                    }

                                                    <span className={`${BLOCK}__label`}>
                                                        <Highlight text={item.label} hits={item.hits} />
                                                    </span>

                                                    {item.hint && <span className={`${BLOCK}__hint`}>{item.hint}</span>}

                                                </div>
                                            );
                                        })
                                    }

                                </div>
                            ))
                    }
                </div>

                <footer className={`${BLOCK}__foot`}>{footerHint}</footer>

            </div>

        </div>,
        document.body,
    );
};
